import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useMasterList } from "@/hooks/useMasterData";

// Lead column targets users can map incoming fields to
const TARGETS = [
  "lead_name", "contact_no", "alternate_no", "email", "company_name",
  "lead_source_detail", "short_description",
  "asset_category_id", "asset_type_id",
  "area_min", "area_max", "area_unit_id",
  "budget_min", "budget_max", "budget_unit_id",
  "preferred_city_ids", "preferred_village_ids", "tag_ids",
  "lead_quality_id", "lead_warmth_id", "next_contact_date",
];

export default function LeadSourceMappings() {
  const qc = useQueryClient();
  const { data: sources = [] } = useMasterList("lead_source");
  const { data: mappings = [] } = useQuery({
    queryKey: ["lead-source-mappings"],
    queryFn: async () => (await api.get("/lead-source-mappings")).data,
  });

  const [selectedSourceId, setSelectedSourceId] = useState("");
  useEffect(() => {
    if (!selectedSourceId && sources.length > 0) setSelectedSourceId(sources[0].id);
  }, [sources, selectedSourceId]);

  const selectedSource = useMemo(() => sources.find((s) => s.id === selectedSourceId), [sources, selectedSourceId]);
  const currentMapping = useMemo(() => mappings.find((m) => m.lead_source_id === selectedSourceId), [mappings, selectedSourceId]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (currentMapping) {
      setRows(Object.entries(currentMapping.mapping || {}).map(([k, v]) => ({ incoming: k, target: v })));
    } else {
      setRows([]);
    }
  }, [currentMapping?.id, selectedSourceId]);

  const save = useMutation({
    mutationFn: async () => {
      const mapping = Object.fromEntries(
        rows.filter((r) => r.incoming && r.target).map((r) => [r.incoming, r.target])
      );
      return api.put("/lead-source-mappings", {
        lead_source_id: selectedSourceId,
        mapping,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lead-source-mappings"] });
      toast.success("Mapping saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/leads-inbound`;
  const samplePayload = useMemo(() => {
    const data = Object.fromEntries(
      rows.filter((r) => r.incoming && r.target).map((r) => [r.incoming, `<value for ${r.target}>`])
    );
    return JSON.stringify({ lead_source: selectedSource?.label || "Meta Ads", data }, null, 2);
  }, [rows, selectedSource]);

  return (
    <div className="p-8 max-w-5xl">
      <div className="label-eyebrow">Admin</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Inbound Lead Mappings</h1>
      <p className="text-sm text-slate-600 mt-1.5 max-w-3xl">
        Map incoming field names (from Meta Ads forms, Google Lead Ads, Sheets, or Zapier/Make middleware)
        to columns on the Leads table. Inbound submissions are POSTed to <code className="font-mono-num bg-slate-100 px-1.5 py-0.5 rounded text-xs">/api/leads-inbound</code> with the <code className="font-mono-num bg-slate-100 px-1.5 py-0.5 rounded text-xs">X-API-Key</code> header. Duplicates (by contact number) are auto-logged as activities on the existing lead.
      </p>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs">Lead Source</Label>
            <Select value={selectedSourceId} onValueChange={setSelectedSourceId}>
              <SelectTrigger className="mt-1" data-testid="mapping-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">Field Mappings</Label>
            <Button size="sm" variant="outline" onClick={() => setRows([...rows, { incoming: "", target: "" }])} data-testid="mapping-add-row">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add row
            </Button>
          </div>
          {rows.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-md p-8 text-center text-sm text-slate-500">
              No mappings yet. Add a row to define <em>incoming_field</em> → <em>lead_column</em>.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 text-[10px] uppercase font-semibold text-slate-500 tracking-wider">
                <div>Incoming Field</div><div></div><div>Lead Column</div><div></div>
              </div>
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
                  <Input
                    value={r.incoming}
                    onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, incoming: e.target.value } : x))}
                    placeholder="e.g. phone_number"
                    className="font-mono-num"
                    data-testid={`mapping-incoming-${i}`}
                  />
                  <div className="text-slate-400">→</div>
                  <Select value={r.target} onValueChange={(v) => setRows(rows.map((x, j) => j === i ? { ...x, target: v } : x))}>
                    <SelectTrigger data-testid={`mapping-target-${i}`}><SelectValue placeholder="Select column…" /></SelectTrigger>
                    <SelectContent>
                      {TARGETS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`mapping-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={() => save.mutate()} disabled={!selectedSourceId} className="bg-slate-900 hover:bg-slate-800" data-testid="mapping-save">
            Save Mapping
          </Button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5">
        <div className="label-eyebrow">Inbound Endpoint</div>
        <div className="mt-3 space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <code className="font-mono-num bg-slate-100 px-2 py-1 rounded text-xs flex-1 truncate">POST {API_URL}</code>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(API_URL); toast.success("Copied"); }} data-testid="copy-endpoint">
              <Copy className="h-3 w-3 mr-1" /> Copy
            </Button>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Headers</div>
            <code className="font-mono-num bg-slate-100 px-2 py-1 rounded text-xs block">
              X-API-Key: leads_inbound_d4f8b2c9a1e7
            </code>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Body (example)</div>
            <pre className="font-mono-num bg-slate-100 px-3 py-2 rounded text-xs overflow-auto">{samplePayload}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}
